/**
 * <TruncatedText> — visually clamp long text to N lines, surface the full
 * value on hover via a Shadcn tooltip.
 *
 * Two modes:
 *  1. **Text mode** — pass `text`; the component renders a clamped <span>
 *     and shows `text` in the tooltip.
 *  2. **Children mode** — pass `children` (e.g. a <Link>) plus `text` for
 *     the tooltip body. The children are wrapped in `truncate` so the
 *     visual still gets clipped at the parent's max-width, but the tooltip
 *     reveals the full string. Used for entity links in table cells.
 *
 * Tooltip is suppressed when text is empty/null — the fallback dash renders
 * without an interactive trigger.
 */
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

export default function TruncatedText({
  text,
  children,
  lines = 2,
  className = '',
  dash = '—',
  maxWidth = '420px',
  testId,
}) {
  const value = (text ?? '').toString().trim();

  // No content → render dash. No tooltip needed.
  if (!value && !children) {
    return <span className={className} data-testid={testId}>{dash}</span>;
  }

  // Children mode (single-line truncate around any node).
  if (children) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`block truncate cursor-help ${className}`} data-testid={testId}>
              {children}
            </span>
          </TooltipTrigger>
          {value && (
            <TooltipContent side="top" className="text-xs whitespace-pre-wrap" style={{ maxWidth }}>
              {value}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Text mode (N-line clamp). Whitelist 1-4 so Tailwind's JIT keeps them.
  const clamp = { 1: 'line-clamp-1', 2: 'line-clamp-2', 3: 'line-clamp-3', 4: 'line-clamp-4' }[lines] || 'line-clamp-2';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`${clamp} ${className} cursor-help`} data-testid={testId}>{value}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs whitespace-pre-wrap" style={{ maxWidth }}>
          {value}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
